import { Injectable, inject, signal } from '@angular/core';
import { StorageService } from '../../services/storage/storage.service';

/**
 * Globaler Store-Name für projektunabhängige OSM-Basiskarten-Tiles (z7-12).
 * Wird von OfflineGridLayer und OfflineLayerService verwendet.
 */
export const GLOBAL_OSM_STORE = '_global_osm';

/** Aktuelle Seed-Version — bei Tile-Updates erhöhen, damit re-geseeded wird */
const SEED_VERSION = 'v1';

/** BBox aus mvbb_std.mbtiles: Ostdeutschland (MV+BB+SN+TH) */
const BBOX = { lonMin: 9.9, latMin: 50.9, lonMax: 15.5, latMax: 55.15 };

const MIN_ZOOM = 7;
const MAX_ZOOM = 12;
const CONCURRENCY = 8;
const OSM_URL_TEMPLATE = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

@Injectable({ providedIn: 'root' })
export class GlobalTileSeedService {
	private storage = inject(StorageService);

	readonly seeding = signal(false);
	readonly progress = signal({ done: 0, total: 0 });

	/**
	 * Seeded globale OSM-Tiles z7-12 falls noch nicht geschehen.
	 * Läuft im Hintergrund, nicht blockierend. Nur bei Netzwerk.
	 */
	async seedIfNeeded(): Promise<void> {
		if (!navigator.onLine) return;

		// Prüfe ob bereits vollständig geseeded
		const version = await this.storage.getProjectValue('_seed_version', GLOBAL_OSM_STORE);
		if (version === SEED_VERSION) return;

		// Alle benötigten Tile-Keys berechnen
		const requiredKeys = this.calculateTileKeys();

		// Bereits vorhandene Keys laden
		const storedKeys = new Set(await this.storage.listKeys(GLOBAL_OSM_STORE) ?? []);

		// Fehlende Keys ermitteln
		const missing = requiredKeys.filter(k => !storedKeys.has(k));
		if (missing.length === 0) {
			// Alle Tiles vorhanden, nur Version setzen
			await this.storage.setProjectValue('_seed_version', GLOBAL_OSM_STORE, SEED_VERSION);
			return;
		}

		this.seeding.set(true);
		this.progress.set({ done: 0, total: missing.length });

		try {
			await this.downloadTiles(missing);
			await this.storage.setProjectValue('_seed_version', GLOBAL_OSM_STORE, SEED_VERSION);
		} catch (err) {
			console.warn('[GlobalTileSeed] Seeding unvollständig:', err);
			// Seed-Version NICHT setzen → nächster Start versucht es erneut
		} finally {
			this.seeding.set(false);
		}
	}

	/**
	 * Berechnet alle Tile-Keys für die BBox und z7-12.
	 */
	private calculateTileKeys(): string[] {
		const keys: string[] = [];
		for (let z = MIN_ZOOM; z <= MAX_ZOOM; z++) {
			const n = Math.pow(2, z);
			const xMin = Math.floor((BBOX.lonMin + 180) / 360 * n);
			const xMax = Math.floor((BBOX.lonMax + 180) / 360 * n);
			const yMin = Math.floor((1 - Math.log(Math.tan(BBOX.latMax * Math.PI / 180) + 1 / Math.cos(BBOX.latMax * Math.PI / 180)) / Math.PI) / 2 * n);
			const yMax = Math.floor((1 - Math.log(Math.tan(BBOX.latMin * Math.PI / 180) + 1 / Math.cos(BBOX.latMin * Math.PI / 180)) / Math.PI) / 2 * n);
			for (let x = xMin; x <= xMax; x++) {
				for (let y = yMin; y <= yMax; y++) {
					keys.push(`${z}_${x}_${y}`);
				}
			}
		}
		return keys;
	}

	private async downloadTiles(keys: string[]): Promise<void> {
		let done = 0;
		const tasks = keys.map(key => async () => {
			const [z, x, y] = key.split('_').map(Number);
			const url = OSM_URL_TEMPLATE.replace('{z}', String(z)).replace('{x}', String(x)).replace('{y}', String(y));
			try {
				const response = await fetch(url);
				if (!response.ok) throw new Error(`HTTP ${response.status}`);
				const blob = await response.blob();
				const dataUrl = await this.blobToBase64(blob);
				await this.storage.setProjectValue(key, GLOBAL_OSM_STORE, dataUrl);
			} catch (err) {
				// Einzelnes Tile fehlgeschlagen — überspringen
				console.warn(`[GlobalTileSeed] Tile ${key} fehlgeschlagen:`, err);
			}
			done++;
			this.progress.set({ done, total: keys.length });
		});

		// Paralleler Download mit Concurrency-Limit
		const running = new Set<Promise<void>>();
		for (const task of tasks) {
			if (!navigator.onLine) break;
			const p = task().finally(() => running.delete(p));
			running.add(p);
			if (running.size >= CONCURRENCY) {
				await Promise.race(running);
			}
		}
		await Promise.all(running);
	}

	private blobToBase64(blob: Blob): Promise<string> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onloadend = () => resolve(reader.result as string);
			reader.onerror = reject;
			reader.readAsDataURL(blob);
		});
	}
}
